import { areaCountryList } from '@/constants';
import type { CountryProps } from '@/constants';
import { formatNumber, formatUnitNumber } from '@/utils/util';
import dayjs from 'dayjs';
import { pinyin } from 'pinyin-pro';

export const FAN_RANGE_MAX = 100000000;
export const PRICE_RANGE_MAX = 100000;
export const DEADLINE_PRESET_MONTHS = [1, 3, 6, 12];

const COMMON_COUNTRY_LIMIT = 5;
const DEADLINE_CALENDAR_HISTORY_MONTHS = 12;

export type CountryNameKey = 'chineseName' | 'englishName' | 'jaName';
export type TagNameKey = 'name' | 'nameEn' | 'nameJa';
export type NumericRange = [number, number];
export type DeadlineRange = [Date, Date] | null;

export interface RangeScale {
  entries: NumericRange[];
  marks: Record<number, string>;
}

interface CountryGroupOptions {
  filterCountry?: (country: CountryProps) => boolean;
  getAdditionalSearchValues?: (country: CountryProps) => Array<string | undefined>;
}

export const formatCountryFlag = (code: string) => {
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(...[...code].map((char) => char.charCodeAt(0) + 127397));
};

export const getCountryName = (country: CountryProps, nameKey: CountryNameKey) => country[nameKey] || country.englishName;

const normalizeKeyword = (value: string) => value.trim().toLocaleLowerCase();

const sortCountriesByName = (countryList: CountryProps[], nameKey: CountryNameKey, locale: string) =>
  [...countryList].sort((a, b) => getCountryName(a, nameKey).localeCompare(getCountryName(b, nameKey), locale));

// 中文拼音分组：取汉字首字的拼音首字母（A-Z），用 pinyin-pro 转换
// 返回大写字母，非汉字（含英文首字）同样取首字母，兜底返回 '#'
export const getChineseGroup = (chineseName: string): string => {
  const firstChar = chineseName.trim()[0];
  if (!firstChar) return '#';
  // pinyin-pro: pattern:'first' 只取声母/首字母，toneType:'none' 不带音调
  const initial = pinyin(firstChar, { pattern: 'first', toneType: 'none' }).toUpperCase();
  // 结果是单个字母时直接用，否则兜底
  return /^[A-Z]$/.test(initial) ? initial : '#';
};

// 50 音行分组：将片假名（及对应平假名）首字符映射到带序号前缀的行名，以保证排序按 50 音序
// 前缀格式 "01_ア行"，显示时截取 "_" 后半部分
const GOJUON_ROWS: Array<{ key: string; chars: string[] }> = [
  { key: '01_ア行', chars: ['ア', 'イ', 'ウ', 'エ', 'オ', 'あ', 'い', 'う', 'え', 'お'] },
  { key: '02_カ行', chars: ['カ', 'キ', 'ク', 'ケ', 'コ', 'ガ', 'ギ', 'グ', 'ゲ', 'ゴ', 'か', 'き', 'く', 'け', 'こ', 'が', 'ぎ', 'ぐ', 'げ', 'ご'] },
  { key: '03_サ行', chars: ['サ', 'シ', 'ス', 'セ', 'ソ', 'ザ', 'ジ', 'ズ', 'ゼ', 'ゾ', 'さ', 'し', 'す', 'せ', 'そ', 'ざ', 'じ', 'ず', 'ぜ', 'ぞ'] },
  { key: '04_タ行', chars: ['タ', 'チ', 'ツ', 'テ', 'ト', 'ダ', 'ヂ', 'ヅ', 'デ', 'ド', 'た', 'ち', 'つ', 'て', 'と', 'だ', 'ぢ', 'づ', 'で', 'ど'] },
  { key: '05_ナ行', chars: ['ナ', 'ニ', 'ヌ', 'ネ', 'ノ', 'な', 'に', 'ぬ', 'ね', 'の'] },
  {
    key: '06_ハ行',
    chars: [
      'ハ',
      'ヒ',
      'フ',
      'ヘ',
      'ホ',
      'バ',
      'ビ',
      'ブ',
      'ベ',
      'ボ',
      'パ',
      'ピ',
      'プ',
      'ペ',
      'ポ',
      'は',
      'ひ',
      'ふ',
      'へ',
      'ほ',
      'ば',
      'び',
      'ぶ',
      'べ',
      'ぼ',
      'ぱ',
      'ぴ',
      'ぷ',
      'ぺ',
      'ぽ',
    ],
  },
  { key: '07_マ行', chars: ['マ', 'ミ', 'ム', 'メ', 'モ', 'ま', 'み', 'む', 'め', 'も'] },
  { key: '08_ヤ行', chars: ['ヤ', 'ユ', 'ヨ', 'や', 'ゆ', 'よ'] },
  { key: '09_ラ行', chars: ['ラ', 'リ', 'ル', 'レ', 'ロ', 'ら', 'り', 'る', 'れ', 'ろ'] },
  { key: '10_ワ行', chars: ['ワ', 'ヲ', 'ン', 'わ', 'を', 'ん'] },
];

// 构建片假名/平假名 → 行 key 的查找表
const KANA_TO_GROUP_KEY = new Map<string, string>(GOJUON_ROWS.flatMap(({ key, chars }) => chars.map((char) => [char, key] as [string, string])));

export const getJapaneseGroup = (jaName: string): string => {
  const firstChar = jaName.trim()[0];
  if (!firstChar) return '99_#';
  return KANA_TO_GROUP_KEY.get(firstChar) ?? '99_#';
};

// 将分组 key（如 "01_ア行"）转换为显示标题（"ア行"）
export const formatGroupTitle = (groupKey: string): string => {
  const separatorIndex = groupKey.indexOf('_');
  return separatorIndex !== -1 ? groupKey.slice(separatorIndex + 1) : groupKey;
};

const createRangeScale = (max: number): RangeScale => {
  const marksNumber = Math.ceil(Math.log10(max)) + 1;
  const entries: NumericRange[] = [];
  const marks: Record<number, string> = {};

  for (let index = 0; index < marksNumber; index += 1) {
    const scaleValue = Math.round((max / (marksNumber - 1)) * index);
    const realValue = scaleValue ? Math.round(max * Math.pow(10, index - (marksNumber - 1))) : 0;
    entries[index] = [scaleValue, realValue];
    marks[scaleValue] = formatUnitNumber({ value: realValue, largeNumberFractionDigits: 0, originFractionDigits: 0 }) as string;
  }

  return { entries, marks };
};

export const ALL_COUNTRIES = areaCountryList.flatMap((area) => area.countryList);
const COUNTRY_BY_ID = new Map(ALL_COUNTRIES.map((country) => [country.countryId, country]));
export const FAN_RANGE_SCALE = createRangeScale(FAN_RANGE_MAX);
export const PRICE_RANGE_SCALE = createRangeScale(PRICE_RANGE_MAX);

export const getCommonCountries = (offerCountryIds: number[], nameKey: CountryNameKey, locale: string) => {
  const countryCountMap = offerCountryIds.reduce<Map<number, number>>((countMap, countryId) => {
    if (COUNTRY_BY_ID.has(countryId)) countMap.set(countryId, (countMap.get(countryId) || 0) + 1);
    return countMap;
  }, new Map());
  const rankedCountries = [...countryCountMap.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([countryId]) => COUNTRY_BY_ID.get(countryId))
    .filter((country): country is CountryProps => Boolean(country));

  if (locale === 'ja-JP') {
    const japan = ALL_COUNTRIES.find((country) => country.twoLetterCode === 'JP');
    const nonJapaneseTopCountries = rankedCountries.filter((country) => country.twoLetterCode !== 'JP').slice(0, COMMON_COUNTRY_LIMIT - 1);
    return [japan, ...sortCountriesByName(nonJapaneseTopCountries, nameKey, locale)].filter((country): country is CountryProps => Boolean(country));
  }

  return sortCountriesByName(rankedCountries.slice(0, COMMON_COUNTRY_LIMIT), nameKey, locale);
};

export const getCountryGroups = (keywordValue: string, nameKey: CountryNameKey, locale: string, options: CountryGroupOptions = {}) => {
  const keyword = normalizeKeyword(keywordValue);
  const filteredCountries = ALL_COUNTRIES.filter((country) => options.filterCountry?.(country) ?? true).filter((country) => {
    if (!keyword) return true;

    return [
      country.chineseName,
      country.englishName,
      country.jaName,
      country.twoLetterCode,
      ...(options.getAdditionalSearchValues?.(country) ?? []),
    ].some((value) => Boolean(value && normalizeKeyword(value).includes(keyword)));
  });

  const isJapanese = locale === 'ja-JP';
  const isChinese = locale === 'zh-CN';

  return sortCountriesByName(filteredCountries, nameKey, locale).reduce<Record<string, CountryProps[]>>((groups, country) => {
    let groupKey: string;
    if (isJapanese) {
      groupKey = getJapaneseGroup(country.jaName);
    } else if (isChinese) {
      groupKey = getChineseGroup(country.chineseName);
    } else {
      groupKey = country.englishName[0]?.toUpperCase() || '#';
    }
    (groups[groupKey] ||= []).push(country);
    return groups;
  }, {});
};

export const transformToRealValue = (value: number, scale: RangeScale, precision = 0) => {
  if (!value) return 0;

  const targetIndex = scale.entries.findIndex(([scaleValue]) => scaleValue >= value);
  if (targetIndex <= 0) return 0;
  if (targetIndex === -1) return scale.entries[scale.entries.length - 1][1];

  const [previousScale, previousReal] = scale.entries[targetIndex - 1];
  const [nextScale, nextReal] = scale.entries[targetIndex];
  let realValue = Number((previousReal + ((nextReal - previousReal) / (nextScale - previousScale)) * (value - previousScale)).toFixed(precision));

  // Slider visual ticks are logarithmic; round the back half of large interpolated numbers so input values stay readable.
  const realValueText = realValue.toString();
  const integerLength = Math.ceil(realValueText.split('.')[0].length / 2);
  const roundedText = realValueText.replace(new RegExp(`(\\d{${integerLength}})(\\d+)`), (...args) => args[1] + '0'.repeat(args[2].length));
  realValue = Number(roundedText);

  return realValue;
};

export const transformToScaleValue = (value: number | undefined, scale: RangeScale) => {
  if (!value) return 0;

  const targetIndex = scale.entries.findIndex(([, realValue]) => realValue >= value);
  if (targetIndex <= 0) return 0;
  if (targetIndex === -1) return scale.entries[scale.entries.length - 1][0];

  const [previousScale, previousReal] = scale.entries[targetIndex - 1];
  const [nextScale, nextReal] = scale.entries[targetIndex];
  return Math.round(previousScale + ((nextScale - previousScale) / (nextReal - previousReal)) * (value - previousReal));
};

const clampRangeValue = (value: number, max: number, precision = 0) => {
  const clampedValue = Math.min(Math.max(value, 0), max);
  return precision ? Number(clampedValue.toFixed(precision)) : Math.round(clampedValue);
};

export const parseRangeInputValue = (value: string, max: number, precision = 0) => {
  const parsedValue = Number(value.replace(/[^\d.]/g, ''));
  return clampRangeValue(Number.isNaN(parsedValue) ? 0 : parsedValue, max, precision);
};

export const formatInputValue = (value: number, precision = 0) => {
  if (precision && !Number.isInteger(value)) return formatNumber(value, precision);
  return formatNumber(value, 0);
};

export const isFullRange = ([minValue, maxValue]: NumericRange, max: number) => minValue <= 0 && maxValue >= max;

export const getSubmittedRangeValues = ([minValue, maxValue]: NumericRange, max: number) => ({
  min: minValue > 0 ? minValue : undefined,
  max: maxValue < max ? maxValue : undefined,
});

export const getDeadlineCalendarMin = (draftDeadlineRange: DeadlineRange) => {
  const defaultMin = dayjs().add(-DEADLINE_CALENDAR_HISTORY_MONTHS, 'M').startOf('month');
  const draftStart = draftDeadlineRange ? dayjs(draftDeadlineRange[0]).startOf('month') : undefined;

  return draftStart?.isValid() && draftStart.isBefore(defaultMin) ? draftStart.toDate() : defaultMin.toDate();
};
